import React, { useEffect, useRef } from 'react';
import './App.css';
import { useAppSelector, useAppDispatch, RootState } from '../../redux';
import { getLastConnectedUser, loadSetting } from '../../redux';
import { AuthVerify, Mercure } from '../../components';
import { useLocation, useNavigate, Outlet, Navigate } from "react-router-dom";
import { Login } from '../Login';
import { Config } from '../../config';
import Topbar from './Topbar';
import { applyThemeClass, normalizeTheme, readStoredTheme, storeTheme } from '../../utils/theme';
import { applyFontFamily, normalizeFontFamily, readStoredFontFamily, storeFontFamily } from '../../utils/fonts';
import { normalizeUiLanguage, pathForHomePage } from '../../utils/uiPreferences';
import i18n from '../../locales/i18n';

import { syncMomentLocale } from '../../locales';
syncMomentLocale();

/** Stable reference — avoids Mercure EventSource reconnect on every App render. */
const MERCURE_TOPICS = ['*'];

function App() {
    const { user, isAuthenticating } = useAppSelector((state : RootState) => state.auth);

    const dispatch     = useAppDispatch();
    const navigate     = useNavigate();
    const { pathname } = useLocation();
    const redirectTo   = localStorage.getItem('redirect-to');
    const homeAppliedRef = useRef(false);
    const themePreference = user?._id
        ? normalizeTheme(user.appearancePreferences?.theme)
        : readStoredTheme();
    const fontPreference = user?._id
        ? normalizeFontFamily(user.appearancePreferences?.fontFamily)
        : readStoredFontFamily();

    useEffect(() => {
        dispatch(getLastConnectedUser());
    }, [dispatch]);

    useEffect(() => {
        if (user?._id)
            dispatch(loadSetting('default'));
    }, [dispatch, user]);

    useEffect(() => {
        applyThemeClass(themePreference);
        storeTheme(themePreference);
        if (themePreference !== 'system' || typeof window === 'undefined' || typeof window.matchMedia !== 'function')
            return;
        const media = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = () => applyThemeClass('system');
        media.addEventListener('change', onChange);
        return () => media.removeEventListener('change', onChange);
    }, [themePreference]);

    useEffect(() => {
        applyFontFamily(fontPreference);
        storeFontFamily(fontPreference);
    }, [fontPreference]);

    useEffect(() => {
        const language = normalizeUiLanguage(user?.uiPreferences?.language);
        if (!language)
            return;
        if (i18n.language === language || i18n.language?.startsWith(`${language}-`))
            return;
        i18n.changeLanguage(language);
    }, [user?.uiPreferences?.language]);

    useEffect(() => {
        if (!user || !('_id' in user))
            return;
        if (!redirectTo)
            return;
        homeAppliedRef.current = true;
        localStorage.removeItem('redirect-to');
        navigate(redirectTo);
    }, [navigate, redirectTo, user]);

    useEffect(() => {
        if (!user || !('_id' in user) || redirectTo || homeAppliedRef.current)
            return;
        if (pathname !== '/')
            return;
        homeAppliedRef.current = true;
        const homePath = pathForHomePage(user.uiPreferences?.homePage);
        if (homePath !== '/')
            navigate(homePath, { replace: true });
    }, [navigate, pathname, redirectTo, user]);

    if (isAuthenticating) {
        return <h3 className="text-gray-900 dark:text-gray-100">...</h3>;
    }

    if (!user || !('_id' in user)) {
        if (pathname !== '/')
            localStorage.setItem('redirect-to', pathname);

        // For card URLs, show the public preview instead of the login page.
        // The private URL is already saved above so login will restore it afterward.
        const changeMatch = pathname.match(/^\/projects\/[^/]+\/change\/([^/]+)/);
        if (changeMatch)
            return <Navigate to={`/public/change/${changeMatch[1]}`} replace />;

        return <Login />;
    }

    return (
        <div className="App flex h-screen flex-col overflow-hidden bg-gray-50 dark:bg-gray-900">
            <div className="shrink-0">
                <Topbar />
            </div>
            <AuthVerify />
            <Mercure hub={Config.mercure_hub} topics={MERCURE_TOPICS} />
            <div className="min-h-0 flex-1 overflow-y-auto" data-app-scroll-root>
                <Outlet />
            </div>
        </div>
    );
}

export default App;
